import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Select,
  Table,
  Popconfirm,
  Row,
  Col,
  message,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import LeaveService from "../../Services/Manage/LeaveService";

const { Option } = Select;
const { TextArea } = Input;

function AddSalary({ user_id, departmentId, addedBy }) {
  const [submittedSalaries, setSubmittedSalaries] = useState([]);
  const [filteredSalaries, setFilteredSalaries] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingSalaryIndex, setEditingSalaryIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [salaryForm] = Form.useForm();

  useEffect(() => {
    if (user_id) {
      fetchSalaries();
    }
  }, [user_id]);

  const fetchSalaries = async () => {
    setLoading(true);
    try {
      const data = await LeaveService.getEmployeeSalary(user_id);
      const salaryArray = Array.isArray(data) ? data : [data];

      const mapped = salaryArray.map((item) => ({
        salaryType: item.salary_type,
        ctc: item.salary_amount,
        workingHours: item.working_hours,
        description: item.description,
        note: item.note,
        id: item.id,
      }));
      setSubmittedSalaries(mapped);
      setFilteredSalaries(mapped);
    } catch (error) {
      message.error("Failed to load salary details.");
    } finally {
      setLoading(false);
    }
  };

  // 🔍 Search handler
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    const filtered = submittedSalaries.filter((item) =>
      Object.values(item).some(
        (val) =>
          val &&
          String(val).toLowerCase().includes(value)
      )
    );

    setFilteredSalaries(filtered);
  };

  const openEditDrawer = (index) => {
    const salary = filteredSalaries[index];
    salaryForm.setFieldsValue(salary);
    setEditingSalaryIndex(index);
    setDrawerVisible(true);
  };

  const onSalaryFinish = async (values) => {
    const salaryWithMeta = {
      salary_type: values.salaryType,
      salary_amount: values.ctc,
      working_hours: values.workingHours,
      description: values.description,
      note: values.note,
      emp_id: user_id,
      added_by: addedBy,
    };

    try {
      if (editingSalaryIndex !== null) {
        const id = filteredSalaries[editingSalaryIndex]?.id;
        await LeaveService.updateEmployeeSalary(id, salaryWithMeta);
        message.success("Salary updated successfully.");
      } else {
        await LeaveService.createEmployeeSalary(salaryWithMeta);
        message.success("Salary added successfully.");
      }

      await fetchSalaries();
      setDrawerVisible(false);
      salaryForm.resetFields();
      setEditingSalaryIndex(null);
    } catch (err) {
      message.error("Error saving salary.");
      console.error("API ERROR:", err);
    }
  };

  const onDeleteSalary = async (index) => {
    const salary = filteredSalaries[index];
    try {
      await LeaveService.deleteEmployeeSalary(salary.id);
      setSubmittedSalaries((prev) => prev.filter((_, i) => i !== index));
      setFilteredSalaries((prev) => prev.filter((_, i) => i !== index));
      message.success("Salary deleted successfully.");
    } catch (error) {
      message.error("Failed to delete salary.");
    }
  };

  const salaryColumns = [
    {
      title: "S.No",
      key: "serial",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    { title: "Salary Type", dataIndex: "salaryType", key: "salaryType" },
    { title: "Salary Amount", dataIndex: "ctc", key: "ctc" },
    { title: "Working Hours", dataIndex: "workingHours", key: "workingHours" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Note", dataIndex: "note", key: "note" },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined style={{ fontSize: 18 }} />}
            onClick={() => openEditDrawer(index)}
            style={{
              marginRight: 8,
              border: "1px solid #d9d9d9",
              borderRadius: 6,
              padding: "4px 8px",
            }}
          />
          <Popconfirm
            title="Are you sure to delete this salary detail?"
            onConfirm={() => onDeleteSalary(index)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined style={{ fontSize: 18 }} />}
              style={{
                marginRight: 8,
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                padding: "4px 8px",
              }}
            />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Card
      title={
        <Row justify="space-between" align="middle" style={{ width: "100%" }}>
          <Col>
            <h3 style={{ margin: 0 }}>Salary Details</h3>
          </Col>
          <Col>
            <Row gutter={12} align="middle">
              <Col>
                <Input
                  placeholder="Search..."
                  value={searchText}
                  onChange={handleSearch}
                  allowClear
                  size="large"
                  style={{ width: 260, fontSize: 16 }}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingSalaryIndex(null);
                    salaryForm.resetFields();
                    setDrawerVisible(true);
                  }}
                >
                  Add Salary
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      }
    >
      <Table
        loading={loading}
        dataSource={filteredSalaries}
        columns={salaryColumns}
        rowKey={(record, index) => index}
        pagination={false}
        locale={{ emptyText: "No salary details added yet." }}
      />

      <Drawer
        title={editingSalaryIndex !== null ? "Edit Salary Details" : "Add Salary Details"}
        width={400}
        onClose={() => {
          setDrawerVisible(false);
          setEditingSalaryIndex(null);
          salaryForm.resetFields();
        }}
        open={drawerVisible}
        destroyOnClose
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form layout="vertical" form={salaryForm} onFinish={onSalaryFinish}>
          <Form.Item
            label="Salary Type"
            name="salaryType"
            rules={[{ required: true, message: "Please select salary type" }]}
          >
            <Select placeholder="Select salary type">
              <Option value="monthly">Monthly</Option>
              <Option value="weekly">Weekly</Option>
              <Option value="daily">Daily</Option>
              <Option value="outsourced">Outsourced</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Salary Amount"
            name="ctc"
            rules={[{ required: true, message: "Please enter CTC" }]}
          >
            <Input placeholder="Enter CTC" />
          </Form.Item>

          <Form.Item
            label="Working Hours"
            name="workingHours"
            rules={[{ required: true, message: "Please enter working hours" }]}
          >
            <Input placeholder="Enter working hours" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <TextArea rows={3} placeholder="Enter Description" />
          </Form.Item>

          <Form.Item label="Note" name="note">
            <TextArea rows={2} placeholder="Optional note" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              Submit
            </Button>
            <Button
              onClick={() => {
                setDrawerVisible(false);
                setEditingSalaryIndex(null);
                salaryForm.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
}

export default AddSalary;
